import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

// Importando os novos Orquestradores de Parse
import { detectAndProcess as processRepasses } from './parsers/repasses';
import { detectAndProcess as processExtratos } from './parsers/extratos';

export const useBankReconciliation = () => {
  const [repassesData, setRepassesData] = useState(null);
  const [extratoData, setExtratoData] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // ===== Estados para gerenciamento de taxas órfãs do iFood =====
  const [pendingOrphans, setPendingOrphans] = useState(null);
  // Guarda os repasses parciais enquanto o usuário resolve os órfãos
  const [stagedRepasses, setStagedRepasses] = useState(null);
  const [stagedAgrupar, setStagedAgrupar] = useState(false);

  // Processar arquivo de repasses delegando para a inteligência de parsers
  const uploadRepasses = useCallback(async (file, agruparPorDia = false) => {
    setLoading(true);
    try {
      let dadosArray;
      if (file.name.toLowerCase().endsWith('.csv')) {
        // Leitor nativo super rápido para CSV (imune a auto-formatação do XLSX)
        const buffer = await file.arrayBuffer();
        // Bancos brasileiros antigos geram ANSI/Latin1, mas plataformas modernas (iFood/Mercado Pago) geram UTF-8
        let text;
        try {
          text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
        } catch (e) {
          text = new TextDecoder('iso-8859-1').decode(buffer); 
        }
        const lines = text.split(/\r?\n/);
        // Detecção inteligente de delimitador
        const firstLine = lines[0] || '';
        const delimiter = firstLine.split(';').length >= firstLine.split(',').length ? ';' : ',';
        dadosArray = lines.map(line => line.split(delimiter).map(cell => cell.trim()));
      } else {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { raw: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        dadosArray = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      }

      // O orquestrador envia a matriz inteira para o Pattern de Factory dos Parsers
      let resultado = processRepasses(dadosArray);

      // Verificar se o parser retornou objeto com órfãos (formato iFood especial)
      if (resultado && !Array.isArray(resultado) && resultado.orphans && resultado.orphans.length > 0) {
        // Há taxas órfãs que precisam de decisão manual do usuário
        setStagedRepasses(resultado.repasses);
        setStagedAgrupar(agruparPorDia);
        setPendingOrphans(resultado.orphans);
        // NÃO finaliza o upload — aguarda o usuário resolver via modal
        return;
      }

      // Sem órfãos: fluxo normal
      let repasses = Array.isArray(resultado) ? resultado : resultado.repasses;

      if (!repasses || repasses.length === 0) {
        throw new Error('Nenhum repasse válido encontrado no arquivo');
      }

      if (agruparPorDia) {
        repasses = agruparRepassesPorDia(repasses);
      }

      setRepassesData(repasses);
    } catch (error) {
      console.error('Erro ao processar repasses:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para o usuário resolver um órfão: escolher em qual lote descontar
  const resolveOrphan = useCallback((orphanId, chosenLotId) => {
    setPendingOrphans(prev => {
      if (!prev) return null;

      const orphan = prev.find(o => o.id === orphanId);
      if (!orphan) return prev;

      // Aplicar o desconto no lote escolhido dentro do stagedRepasses
      setStagedRepasses(prevRepasses => {
        if (!prevRepasses) return prevRepasses;
        if (chosenLotId === null) return prevRepasses; // Usuário decidiu Pular

        return prevRepasses.map(r => {
          if (r.idSaldo === chosenLotId) {
            return {
              ...r,
              valor: Math.round((r.valor + orphan.valor) * 100) / 100 // orphan.valor é negativo
            };
          }
          return r;
        }).filter(r => r.valor > 0); // Remover se ficou zero ou negativo
      });

      // Remover o órfão resolvido da fila
      const remaining = prev.filter(o => o.id !== orphanId);
      
      if (remaining.length === 0) {
        // Todos os órfãos foram resolvidos — finalizar o upload
        // Usamos um timeout mínimo para que o setStagedRepasses acima tenha efeito
        setTimeout(() => {
          setStagedRepasses(currentRepasses => {
            if (currentRepasses) {
              let finalRepasses = currentRepasses;
              // Aplicar agrupamento se necessário (consultando stagedAgrupar via closure)
              setRepassesData(finalRepasses);
            }
            return null;
          });
          setPendingOrphans(null);
          setLoading(false);
        }, 50);
        return null;
      }

      return remaining;
    });
  }, []);

  // Função utilitária para agrupar repasses por dia
  const agruparRepassesPorDia = (repasses) => {
    const groupedMap = new Map();
    
    repasses.forEach(repasse => {
      const tipoStr = repasse.tipo || 'Desconhecido';
      const key = `${repasse.data}-${tipoStr}`;
      
      if (groupedMap.has(key)) {
        const existing = groupedMap.get(key);
        existing.valor += repasse.valor;
        existing.agrupados += 1;
      } else {
        groupedMap.set(key, {
          ...repasse,
          agrupados: 1,
          isAgrupado: true
        });
      }
    });

    return Array.from(groupedMap.values());
  };

  // Processar arquivo de extrato delegando para a inteligência de parsers
  const uploadExtrato = useCallback(async (file) => {
    setLoading(true);
    try {
      let dadosArray;
      if (file.name.toLowerCase().endsWith('.csv')) {
        // Leitor nativo super rápido para CSV (imune a auto-formatação do XLSX)
        const buffer = await file.arrayBuffer();
        // Bancos brasileiros antigos geram ANSI/Latin1, mas plataformas modernas (iFood/Mercado Pago) geram UTF-8
        let text;
        try {
          text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
        } catch (e) {
          text = new TextDecoder('iso-8859-1').decode(buffer); 
        }
        const lines = text.split(/\r?\n/);
        // Detecção inteligente de delimitador
        const firstLine = lines[0] || '';
        const delimiter = firstLine.split(';').length >= firstLine.split(',').length ? ';' : ',';
        dadosArray = lines.map(line => line.split(delimiter).map(cell => cell.trim()));
      } else {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { raw: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        dadosArray = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      }

      // O orquestrador envia a matriz inteira para o Pattern de Factory dos Parsers
      const extrato = processExtratos(dadosArray);

      if (!extrato || extrato.length === 0) {
        throw new Error('Nenhum crédito válido encontrado no extrato');
      }

      setExtratoData(extrato);
    } catch (error) {
      console.error('Erro ao processar extrato:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Comparar dados - Lógica agnóstica de cruzamento permanece inalterada
  const compareData = useCallback(() => {
    if (!repassesData || repassesData.length === 0) {
      throw new Error('Dados de repasses não encontrados');
    }

    if (!extratoData || extratoData.length === 0) {
      throw new Error('Dados de extrato não encontrados');
    }

    const encontrados = [];
    const naoEncontrados = [];
    const extratoUsados = new Set(); // Registra os índices do extrato que já foram "casados"

    repassesData.forEach(repasse => {
      // Procurar valor exato na mesma data que AINDA NÃO foi usado
      const matchIndex = extratoData.findIndex((e, index) => {
        return !extratoUsados.has(index) && 
               e.data === repasse.data && 
               Math.abs(e.valor - repasse.valor) < 0.01; // Tolerância de 1 centavo
      });

      if (matchIndex !== -1) {
        extratoUsados.add(matchIndex); // Marca a linha do extrato como "usada"
        encontrados.push({
          repasse: repasse,
          extrato: extratoData[matchIndex],
          status: 'encontrado'
        });
      } else {
        naoEncontrados.push({
          repasse: repasse,
          status: 'nao_encontrado'
        });
      }
    });

    // Calcular totais
    const totalRepasses = repassesData.reduce((sum, r) => sum + r.valor, 0);
    const totalExtrato = extratoData.reduce((sum, e) => sum + e.valor, 0);
    const totalEncontrados = encontrados.reduce((sum, e) => sum + e.repasse.valor, 0);
    const totalNaoEncontrados = naoEncontrados.reduce((sum, n) => sum + n.repasse.valor, 0);

    const resultado = {
      encontrados: encontrados,
      naoEncontrados: naoEncontrados,
      totais: {
        repasses: totalRepasses,
        extrato: totalExtrato,
        encontrados: totalEncontrados,
        naoEncontrados: totalNaoEncontrados,
        percentualEncontrado: totalRepasses > 0 ? (totalEncontrados / totalRepasses) * 100 : 0
      },
      bate100: naoEncontrados.length === 0
    };

    setComparisonResult(resultado);
  }, [repassesData, extratoData]);

  // Limpar dados
  const clearData = useCallback(() => {
    setRepassesData(null);
    setExtratoData(null);
    setComparisonResult(null);
    setPendingOrphans(null);
    setStagedRepasses(null);
  }, []);

  return {
    repassesData,
    extratoData,
    comparisonResult,
    loading,
    uploadRepasses,
    uploadExtrato,
    compareData,
    clearData,
    // Novos exports para o modal de órfãos
    pendingOrphans,
    resolveOrphan
  };
};

